import { useState } from 'react'
import { Database, Wifi, ArrowRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  type AppMode,
  writeStoredPreference,
} from '@/lib/env'
import { cn } from '@/lib/utils'

/**
 * Tela de boas-vindas exibida no PRIMEIRO boot — quando o usuário ainda
 * não escolheu explicitamente um modo (sem `vectraclip-mode-preference`
 * no `localStorage`).
 *
 * Por que é fullscreen e bloqueante:
 * - A escolha define se o MSW worker é registrado e qual `apiBaseUrl` o
 *   `apiClient` vai usar. Mostrar o app antes da decisão renderizaria com
 *   o default do build, o que confunde o usuário no exato momento em que
 *   estamos perguntando.
 *
 * Após escolher → grava preferência → `location.reload()` para que o
 * `bootstrap()` em `main.tsx` reentre já com a preferência aplicada.
 */
export interface FirstBootModePickerProps {
  /** URL default sugerida quando o usuário escolhe "API real". */
  defaultApiBaseUrl: string
  /** Em qual modo o `.env` do build veio. Ajuda a destacar a "recomendada". */
  envDefaultMode: AppMode
  /** Callback override do reload (testes). */
  onApply?: () => void
}

export function FirstBootModePicker({
  defaultApiBaseUrl,
  envDefaultMode,
  onApply,
}: FirstBootModePickerProps) {
  const [chosen, setChosen] = useState<AppMode>(envDefaultMode)
  const [apiUrl, setApiUrl] = useState(defaultApiBaseUrl)
  const apiUrlValid = chosen === 'mocks' || apiUrl.trim().length > 0

  function handleApply() {
    writeStoredPreference({
      mode: chosen,
      apiBaseUrl: chosen === 'api' ? apiUrl.trim() : undefined,
    })
    if (onApply) onApply()
    else window.location.reload()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-boot-title"
      className="min-h-screen w-full bg-background text-foreground"
    >
      <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-10">
        <div className="mb-8">
          <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            VectraClip · primeiro boot
          </div>
          <h1
            id="first-boot-title"
            className="text-2xl font-semibold tracking-tight"
          >
            Como você quer rodar o app?
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Você pode mudar a qualquer momento clicando no badge no canto
            inferior direito.
          </p>
        </div>

        <div className="space-y-3">
          <ModeOption
            mode="mocks"
            chosen={chosen}
            recommended={envDefaultMode === 'mocks'}
            icon={Database}
            title="Mocks (MSW)"
            description="Worker local responde tudo. Zero dependência de backend. Bom para apresentar o app, validar UI e fluxos."
            onChoose={() => setChosen('mocks')}
          />
          <ModeOption
            mode="api"
            chosen={chosen}
            recommended={envDefaultMode === 'api'}
            icon={Wifi}
            title="API real"
            description="Conecta no backend de verdade. Use quando quiser dados reais ou estiver integrando contra OpenClaw/Supabase."
            onChoose={() => setChosen('api')}
          />
        </div>

        {chosen === 'api' ? (
          <div className="mt-6 space-y-2">
            <Label htmlFor="api-url" className="text-xs uppercase tracking-wider">
              URL base da API
            </Label>
            <Input
              id="api-url"
              type="url"
              placeholder="http://localhost:8080"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Sem `https://` o navegador trata como path relativo. Inclua
              o esquema completo.
            </p>
          </div>
        ) : null}

        <div className="mt-8 flex items-center justify-end gap-3">
          <Button onClick={handleApply} disabled={!apiUrlValid} size="lg">
            Aplicar e abrir
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

interface ModeOptionProps {
  mode: AppMode
  chosen: AppMode
  recommended: boolean
  icon: typeof Database
  title: string
  description: string
  onChoose: () => void
}

function ModeOption({
  mode,
  chosen,
  recommended,
  icon: Icon,
  title,
  description,
  onChoose,
}: ModeOptionProps) {
  const selected = mode === chosen
  return (
    <button
      type="button"
      onClick={onChoose}
      aria-pressed={selected}
      className={cn(
        'group flex w-full items-start gap-4 rounded-lg border p-4 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5'
          : 'border-border bg-surface/40 hover:border-border-strong hover:bg-surface/70',
      )}
    >
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-md',
          selected
            ? 'bg-primary/15 text-primary'
            : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{title}</span>
          {recommended ? (
            <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              recomendado pelo .env
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
    </button>
  )
}
